import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Ticket, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";

const TABS = [
  { key: "home", label: "Home", icon: Home },
  { key: "tours", label: "Tours", icon: Ticket },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
];

export const BottomNav = ({ value, onChange, onLongPress, homeBadge = 0 }) => {
  // Auto-hide while scrolling down, reveal on scroll up (or near the top).
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  // Long-press: hold a tab for a shortcut (Calendar→Today, Tours→this week, Home→refresh).
  const pressTimer = useRef(null);
  const longFired = useRef(false);
  const startPress = (key) => {
    longFired.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longFired.current = true;
      if (navigator.vibrate) { try { navigator.vibrate(15); } catch { /* ignore */ } }
      (onLongPress || onChange)(key);
    }, 500);
  };
  const cancelPress = () => clearTimeout(pressTimer.current);
  const handleTap = (key, e) => {
    if (longFired.current) { e.preventDefault(); longFired.current = false; return; }
    onChange(key);
  };
  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = (e) => {
      const t = e && e.target;
      let y;
      if (!t || t === document || t === window || t === document.documentElement || t === document.body) {
        y = window.scrollY;
      } else if (typeof t.scrollTop === "number") {
        y = t.scrollTop;
      } else {
        y = window.scrollY;
      }
      if (y < 12) setHidden(false);
      else if (y > lastY.current + 8) setHidden(true);
      else if (y < lastY.current - 8) setHidden(false);
      lastY.current = y;
    };
    // capture:true so scrolls inside inner overflow containers (e.g. calendar list) also count.
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  return (
    <nav
      className={`pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 transition-transform duration-300 ${hidden ? "translate-y-[140%]" : "translate-y-0"}`}
      data-testid="bottom-nav"
    >
      <div className="lg-glass pointer-events-auto mx-auto flex max-w-md items-stretch justify-around gap-1 rounded-full px-1.5 py-1">
        {TABS.map((t) => {
          const active = value === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={(e) => handleTap(t.key, e)}
              onPointerDown={() => startPress(t.key)}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onPointerCancel={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
              data-testid={`nav-${t.key}`}
              className={`relative flex flex-1 select-none flex-col items-center gap-1 rounded-full py-1.5 transition-[color,background-color] duration-200 active:scale-95 ${
                active ? "bg-[#9B2226]/15 text-[#E0575B]" : "text-zinc-400 hover:text-zinc-300"
              }`}
            >
              <div className="relative">
                <Icon className={`h-5 w-5 ${active ? "fill-[#E0575B]/15" : ""}`} />
                {t.key === "home" && homeBadge > 0 && (
                  <span className="absolute -right-2.5 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-700 text-zinc-950" data-testid="home-needs-badge">
                    {homeBadge}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-500">{t.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Bottom nav for standalone pages (guest profile / tour detail): tapping a tab
// sets the target Dashboard tab and navigates home, mirroring the Dashboard nav rules.
export const AppBottomNav = () => {
  const navigate = useNavigate();
  const [badge, setBadge] = useState(0);
  useEffect(() => {
    api.needsUpdateCount().then((r) => setBadge(r.count || 0)).catch(() => {});
  }, []);
  const go = (key) => {
    sessionStorage.setItem("activeTab", key);
    navigate("/");
  };
  const onLong = (key) => {
    const map = { calendar: "calendar-today", tours: "tours-week", home: "home-refresh" };
    if (map[key]) sessionStorage.setItem("sd:navIntent", map[key]);
    sessionStorage.setItem("activeTab", key);
    navigate("/");
  };
  return <BottomNav value={null} onChange={go} onLongPress={onLong} homeBadge={badge} />;
};
